"use client";

import { Accordion } from "@base-ui/react/accordion";
import type { ReactNode } from "react";

import { ExpandMoreIcon } from "../../../_components/dx-icons";

import { DxSectionHeader } from "./dx-section-header";

/** DxSectionHeader を内包する折りたたみセクション。 折りたたみ無しと見出し帯が完全一致する。 */
export function DxCollapsibleSection({
  title,
  trailing,
  defaultOpen = false,
  children,
}: {
  title: string;
  /** chevron の外側 (タイトルから遠い側) に置く補助アクション (例: 地図リンク / 外部リンク) */
  trailing?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <Accordion.Root defaultValue={defaultOpen ? ["section"] : []}>
      {/* 開いた時だけ py-16 で Figma 通り (中身の有無で縦余白が変わる)。
       * transition-[padding] で panel の height 拡縮と同じ 200ms にし、 header が
       * ガタッと落ちる視覚を防ぐ。 */}
      <Accordion.Item
        value="section"
        className="group/section transition-[padding] duration-200 data-[open]:py-[16px]"
      >
        {/* Trigger を absolute で row 全体に被せて、 どこをクリックしても開閉できるようにする。
         * 可視 content は pointer-events:none で背面 trigger に click を通す。
         * trailing (例: Google Maps) だけは pointer-events:auto で自前 click を捕捉。 */}
        <Accordion.Header className="relative">
          <Accordion.Trigger
            // 明示 base + hover で AreaSidebar の拠点 item hover と同じ feel に揃える
            // (surface-base #faf9f6 → bg-base-hover #edebe4 の遷移)。
            className="focus-visible:ring-dx-text-default bg-dx-surface-base lg:hover:bg-dx-bg-base-hover lg:group-data-[open]/section:hover:bg-dx-surface-base absolute inset-0 cursor-pointer transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-inset"
            aria-label={title}
          />
          <div className="pointer-events-none relative">
            <DxSectionHeader
              title={<span>{title}</span>}
              actions={
                <>
                  {/* インジケータ (chevron) はタイトルに最も近い位置に置く (DXPJ-62 レビュー)。
                   * chevron は trigger ではなく可視 indicator のみ。 click は背面 trigger。
                   * expand のみ 48px (Size=medium → icon 24px)、 他のアクションは 40px
                   * (Size=small → icon 20px)。 アイコンボタン間の余白は 0 (DXPJ-62 W9TA9T)。 */}
                  <span className="text-dx-text-default flex size-[48px] shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-data-[open]/section:rotate-180">
                    <ExpandMoreIcon size={24} />
                  </span>
                  {trailing !== undefined && (
                    <span className="pointer-events-auto inline-flex items-center">
                      {trailing}
                    </span>
                  )}
                </>
              }
            />
          </div>
        </Accordion.Header>
        <Accordion.Panel className="h-[var(--accordion-panel-height)] overflow-hidden transition-[height] duration-200 data-[ending-style]:h-0 data-[starting-style]:h-0">
          {children}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
}
